/**
=========================================================
* Argon Dashboard 2 MUI - v3.0.1
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-material-ui
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// Argon Dashboard 2 MUI base styles
import typography from "assets/theme/base/typography";

const WITHDRAWAL_COUNTS_VARIANT = "withdrawalCounts";

function externalTooltipHandler(context) {
  let tooltipEl = document.getElementById("chartjs-tooltip");
  if (!tooltipEl) {
    tooltipEl = document.createElement("div");
    tooltipEl.id = "chartjs-tooltip";
    tooltipEl.style.background = "rgba(40,40,40,0.95)";
    tooltipEl.style.borderRadius = "4px";
    tooltipEl.style.color = "#fff";
    tooltipEl.style.pointerEvents = "none";
    tooltipEl.style.position = "absolute";
    tooltipEl.style.transition = "all .1s ease";
    tooltipEl.style.zIndex = "9999";
    tooltipEl.style.padding = "8px 12px";
    tooltipEl.style.fontFamily = "inherit";
    tooltipEl.style.fontSize = "12px";
    tooltipEl.style.minWidth = "0";
    tooltipEl.style.maxWidth = "180px";
    tooltipEl.style.boxShadow = "0 2px 8px rgba(0,0,0,0.06)";
    tooltipEl.style.display = "block";
    document.body.appendChild(tooltipEl);
  }
  const oldCarets = tooltipEl.querySelectorAll(".chartjs-tooltip-caret");
  oldCarets.forEach((c) => c.remove());
  const tooltipModel = context.tooltip;
  if (tooltipModel.opacity === 0) {
    tooltipEl.style.opacity = 0;
    tooltipEl.style.display = "none";
    return;
  }
  tooltipEl.style.display = "block";
  const title = tooltipModel.title[0] || "";
  const bodyLines = tooltipModel.body
    .map((b) => b.lines)
    .flat()
    .filter((line) => line != null && line !== "");
  const colorBoxHTML = `<span style="display:inline-block;width:8px;height:8px;background-color:#007bff;border-radius:50%;margin-right:4px;vertical-align:middle;flex-shrink:0;"></span>`;
  const rowsHtml = bodyLines
    .map(
      (line) =>
        `<div style="display:flex;align-items:center;margin-bottom:2px;">${colorBoxHTML}<span>${line}</span></div>`
    )
    .join("");
  tooltipEl.innerHTML = `
              <div style="font-weight:bold;font-size:1em;margin-bottom:4px;">${title}</div>
              ${rowsHtml}
            `;
  const position = context.chart.canvas.getBoundingClientRect();
  const tooltipHeight = tooltipEl.offsetHeight;
  const tooltipWidth = tooltipEl.offsetWidth;
  const caretX = position.left + window.pageXOffset + tooltipModel.caretX;
  const caretY = position.top + window.pageYOffset + tooltipModel.caretY;

  const tooltipRightPosition = caretX + 11 + tooltipWidth;
  const chartRightPosition = position.left + position.width;
  const showOnLeft = tooltipRightPosition > chartRightPosition;

  if (showOnLeft) {
    tooltipEl.style.left = caretX - tooltipWidth - 11 + "px";
    tooltipEl.style.top = caretY - tooltipHeight / 2 + "px";
  } else {
    tooltipEl.style.left = caretX + 11 + "px";
    tooltipEl.style.top = caretY - tooltipHeight / 2 + "px";
  }

  tooltipEl.style.opacity = 1;

  const caret = document.createElement("div");
  caret.className = "chartjs-tooltip-caret";
  caret.style.position = "absolute";
  caret.style.top = "50%";
  caret.style.transform = "translateY(-50%)";
  caret.style.width = "0";
  caret.style.height = "0";
  caret.style.borderTop = "4px solid transparent";
  caret.style.borderBottom = "4px solid transparent";

  if (showOnLeft) {
    caret.style.right = "-8px";
    caret.style.borderLeft = "8px solid rgba(40,40,40,0.95)";
  } else {
    caret.style.left = "-8px";
    caret.style.borderRight = "8px solid rgba(40,40,40,0.95)";
  }

  tooltipEl.appendChild(caret);
}

function configs(labels, datasets) {
  const isWithdrawalCountChart = datasets[0]?.variant === WITHDRAWAL_COUNTS_VARIANT;
  const countValues = (datasets[0]?.data || []).map((v) => Number(v) || 0);
  const dataMaxCount = countValues.length ? Math.max(0, ...countValues) : 0;
  const yMaxCounts = dataMaxCount === 0 ? 1 : Math.ceil(dataMaxCount * 1.2);
  const yStepCounts = yMaxCounts <= 25 ? 1 : Math.max(1, Math.ceil(yMaxCounts / 12));

  const sharedGridY = {
    drawBorder: false,
    display: true,
    drawOnChartArea: true,
    drawTicks: false,
    borderDash: [5, 5],
  };

  const sharedFontTicks = {
    display: true,
    color: "#b2b9bf",
    padding: 10,
    font: {
      size: 11,
      family: typography.fontFamily,
      style: "normal",
      lineHeight: 2,
    },
  };

  const yScale = isWithdrawalCountChart
    ? {
        type: "linear",
        display: true,
        position: "left",
        min: 0,
        max: yMaxCounts,
        title: {
          display: true,
          text: "Número de retiros",
        },
        grid: sharedGridY,
        ticks: {
          ...sharedFontTicks,
          stepSize: yStepCounts,
          precision: 0,
          callback(value) {
            if (Number.isInteger(value)) {
              return value;
            }
            return "";
          },
        },
      }
    : {
        type: "linear",
        display: true,
        position: "left",
        min: 0,
        title: {
          display: true,
          text: "Monto Retirado ($)",
        },
        grid: sharedGridY,
        ticks: {
          ...sharedFontTicks,
          callback(value) {
            return "$" + value.toLocaleString();
          },
        },
      };

  const xScale = isWithdrawalCountChart
    ? {
        title: {
          display: true,
          text: "Días",
        },
        grid: {
          drawBorder: false,
          display: false,
          drawOnChartArea: false,
          drawTicks: false,
          borderDash: [5, 5],
        },
        ticks: {
          display: true,
          color: "#b2b9bf",
          padding: 20,
          maxRotation: 45,
          minRotation: 45,
          font: {
            size: 11,
            family: typography.fontFamily,
            style: "normal",
            lineHeight: 2,
          },
          callback(val, index) {
            const chartLabels = this.chart?.data?.labels || [];
            const n = chartLabels.length;
            if (n <= 1) {
              return this.getLabelForValue(val);
            }
            if (index === 0 || index === n - 1) {
              return this.getLabelForValue(val);
            }
            return "";
          },
        },
      }
    : {
        title: {
          display: true,
          text: "Días del Mes",
        },
        grid: {
          drawBorder: false,
          display: false,
          drawOnChartArea: false,
          drawTicks: false,
          borderDash: [5, 5],
        },
        ticks: {
          display: true,
          color: "#b2b9bf",
          padding: 20,
          font: {
            size: 11,
            family: typography.fontFamily,
            style: "normal",
            lineHeight: 2,
          },
        },
      };

  const tooltipCallbacks = isWithdrawalCountChart
    ? {
        label(context) {
          const n = context.parsed.y;
          return [`Número de retiros: ${n}`];
        },
      }
    : {
        label(context) {
          const value = context.parsed.y;
          const dataset = context.dataset;
          const index = context.dataIndex;
          const cantidad = dataset.countsByDay ? dataset.countsByDay[index] : undefined;
          if (cantidad !== undefined) {
            return [
              `Importe total: $${value.toLocaleString()}`,
              `Número de retiros: ${cantidad}`,
            ];
          }
          return [`$${value.toLocaleString()}`];
        },
      };

  return {
    data: {
      labels,
      datasets: datasets.map(({ variant: _v, ...rest }) => ({ ...rest })),
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          enabled: false,
          position: "nearest",
          external: externalTooltipHandler,
          callbacks: tooltipCallbacks,
        },
      },
      interaction: {
        intersect: false,
        mode: "index",
      },
      scales: {
        y: yScale,
        x: xScale,
      },
    },
  };
}

export default configs;
